
import {
  defineComponent, reactive, watch, h, computed,
} from 'vue';
import {
  Form, Input, Table, Row, Col, Modal, Skeleton, Switch,
} from 'ant-design-vue';
import { useTelaBase } from '@/core/composables/TelaBase';
import { useModalBase } from '@/core/composables/ModalBase';
import { ISimulacaoProducao } from '@/models/Entidades/PCP/ISimulacaoProducao';
import { ISimulacaoProducaoItem } from '@/models/Entidades/PCP/ISimulacaoProducaoItem';
import { ISimulacaoProducaoNecessidade } from '@/models/Entidades/PCP/ISimulacaoProducaoNecessidade';
import ServicoPCP from '@/servicos/PCP/ServicoPCP';
import storeSistema from '@/store/storeSistema';
import SelecionarEngenhariaProducao from '@/components/Cadastros/PCP/SelecionarEngenhariaProducao.vue';
import SelecionarTipoProducao from '@/components/Cadastros/PCP/SelecionarTipoProducao.vue';
import UtilitarioMascara from '@/core/utilitarios/UtilitarioMascara';
import { ITelaOperacao } from '@/core/models/ITelaOperacao';
import { EPermissaoDados } from '@/models/Enumeradores/MeuSistema/Usuarios/EPermissaoDados';
import Icone from '@/core/components/Icone.vue';
import CampoNumerico from '@/core/components/Tela/CampoNumerico.vue';
import { useGradeBase } from '@/core/composables/GradeBase';
import { ECustomRenderRow, IColumn } from '@/core/models/AntDesign/IColumn';
import { EStatusRetornoRequisicao } from '@/core/models/IRetornoRequisicao';
import { IArquivoPdf } from '@/core/models/IArquivoPdf';
import PreviewPdf from '@/core/components/Modal/PreviewPdf.vue';

// Tipo local estendido para incluir a nova propriedade
interface ISimulacaoProducaoItemLocal extends ISimulacaoProducaoItem {
  codigoEngenharia: number;
}

export default defineComponent({
  name: 'SimulacaoProducaoModal',
  props: {
    visivel: {
      type: Boolean,
    },
    operacao: {
      type: Object as () => ITelaOperacao,
      required: true,
    },
  },
  components: {
    AForm: Form,
    AFormItem: Form.Item,
    AInput: Input,
    ATable: Table,
    ARow: Row,
    ACol: Col,
    CampoNumerico,
    SelecionarEngenhariaProducao,
    SelecionarTipoProducao,
    AModal: Modal,
    ASkeleton: Skeleton,
    ASwitch: Switch,
    Icone,
    PreviewPdf,
  },
  emits: ['update:operacao', 'update:visivel', 'update:confirmacao'],
  setup(props, { emit }) {
    const {
      telaBase, apresentarMensagemSucesso, apresentarMensagemErro, apresentarMensagemAlerta,
    } = useTelaBase();
    const { modalBase } = useModalBase(props, emit);
    const { gradeBase: gradeItens } = useGradeBase();
    const { gradeBase: gradeNecessidades } = useGradeBase();
    const servicoPCP = new ServicoPCP();

    const state = reactive({
      simulacao: {} as ISimulacaoProducao,
      novoItem: {} as ISimulacaoProducaoItemLocal,
      arquivosPdf: [] as IArquivoPdf[],
    });

    const formattedDataSimulacao = computed(() => {
      if (state.simulacao.dataSimulacao) {
        return new Date(state.simulacao.dataSimulacao).toLocaleDateString('pt-BR');
      }
      return '';
    });

    const custoTotalNecessario = computed(() => state.simulacao.necessidadesCalculadas.reduce((total, necessidade) => total + (necessidade.custoTotal || 0), 0));

    const colunasItens = computed(() => (gradeItens.colunas.filter((c) => c.visible) as any));
    const colunasNecessidades = computed(() => (gradeNecessidades.colunas.filter((c) => c.visible) as any));

    function preencherColunas() {
      gradeItens.colunas = [
        {
          title: 'Produto',
          dataIndex: 'descricaoProduto',
          key: 'descricaoProduto',
          position: 1,
          visible: true,
        },
        {
          title: 'Quantidade',
          dataIndex: 'quantidadeSimulada',
          key: 'quantidadeSimulada',
          align: 'right' as any,
          position: 2,
          visible: true,
          customRenderRow: ECustomRenderRow.MascararFracionado,
        },
        {
          title: 'Estoque Atual',
          dataIndex: 'estoqueMomento',
          key: 'estoqueMomento',
          align: 'right' as any,
          position: 3,
          visible: true,
          customRenderRow: ECustomRenderRow.MascararFracionado,
        },
        {
          title: 'Ações',
          key: 'acoes',
          position: 4,
          visible: true,
        },
      ] as unknown as IColumn[];

      gradeNecessidades.colunas = [
        {
          title: 'Código Produto',
          dataIndex: 'codigoProduto',
          key: 'codigoProduto',
          position: 0,
          visible: false,
        },
        {
          title: 'Produto (Componente)',
          dataIndex: 'descricaoProduto',
          key: 'descricaoProduto',
          position: 1,
          visible: true,
          sorter: (a: ISimulacaoProducaoNecessidade, b: ISimulacaoProducaoNecessidade) => (a.descricaoProduto || '').localeCompare((b.descricaoProduto || '')),
        },
        {
          title: 'Necessário',
          dataIndex: 'quantidadeNecessaria',
          key: 'quantidadeNecessaria',
          align: 'right' as any,
          position: 2,
          visible: true,
          customRenderRow: ECustomRenderRow.MascararFracionado,
          sorter: (a: ISimulacaoProducaoNecessidade, b: ISimulacaoProducaoNecessidade) => (Number(a.quantidadeNecessaria) || 0) - (Number(b.quantidadeNecessaria) || 0),
        },
        {
          title: 'Estoque',
          dataIndex: 'saldoEstoqueMomento',
          key: 'quantidadeEstoque',
          align: 'right' as any,
          position: 3,
          visible: true,
          customRenderRow: ECustomRenderRow.MascararFracionado,
          sorter: (a: ISimulacaoProducaoNecessidade, b: ISimulacaoProducaoNecessidade) => (Number(a.saldoEstoqueMomento) || 0) - (Number(b.saldoEstoqueMomento) || 0),
        },
        {
          title: 'Comprar',
          key: 'quantidadeComprar',
          align: 'right' as any,
          position: 4,
          visible: true,
          sorter: (a: ISimulacaoProducaoNecessidade, b: ISimulacaoProducaoNecessidade) => {
            const comprarA = Math.max((Number(a.quantidadeNecessaria) || 0) - (Number(a.saldoEstoqueMomento) || 0), 0);
            const comprarB = Math.max((Number(b.quantidadeNecessaria) || 0) - (Number(b.saldoEstoqueMomento) || 0), 0);
            return comprarA - comprarB;
          },
          customRender: ({ record }: { record: ISimulacaoProducaoNecessidade }) => {
            const aComprar = record.quantidadeNecessaria - record.saldoEstoqueMomento;
            const valor = aComprar > 0 ? aComprar : 0;
            return h('span', UtilitarioMascara.mascararValor(valor, 4));
          },
        },
        {
          title: 'Custo Unitário',
          dataIndex: 'custo',
          key: 'custo',
          align: 'right' as any,
          position: 5,
          visible: true,
          customRenderRow: ECustomRenderRow.MascararFracionado,
          sorter: (a: ISimulacaoProducaoNecessidade, b: ISimulacaoProducaoNecessidade) => (Number(a.custo) || 0) - (Number(b.custo) || 0),
        },
        {
          title: 'Custo Total',
          dataIndex: 'custoTotal',
          key: 'custoTotal',
          align: 'right' as any,
          position: 6,
          visible: true,
          customRenderRow: ECustomRenderRow.MascararFracionado,
          sorter: (a: ISimulacaoProducaoNecessidade, b: ISimulacaoProducaoNecessidade) => (Number(a.custoTotal) || 0) - (Number(b.custoTotal) || 0),
        },
      ] as IColumn[];
    }

    function objetoNovoItem(): ISimulacaoProducaoItemLocal {
      return {
        codigo: 0,
        codigoSimulacao: state.simulacao.codigo,
        codigoEngenharia: 0,
        descricaoProduto: '',
        quantidadeSimulada: 0,
        estoqueMomento: 0,
      };
    }

    function objetoInicial() {
      state.simulacao = {
        codigo: 0,
        codigoEmpresa: storeSistema.getters.codigoEmpresaOperacao(),
        dataSimulacao: new Date().toISOString(),
        codigoUsuario: storeSistema.getters.codigoUsuarioAutenticado(),
        descricao: '',
        observacao: '',
        status: 0,
        profundidadeTotal: false,
        codigoTipoProducaoPI: 0,
        itensSimulados: [],
        necessidadesCalculadas: [],
      } as unknown as ISimulacaoProducao;
      state.novoItem = objetoNovoItem();
    }

    async function atualizarTela() {
      telaBase.carregando = true;
      objetoInicial();
      if (modalBase.computedVisivel) {
        if (props.operacao.tipoPermissaoDados === EPermissaoDados.Visualizar || props.operacao.tipoPermissaoDados === EPermissaoDados.Alterar) {
          state.simulacao = await servicoPCP.obterSimulacaoPorCodigo(props.operacao.codigoRegistro, props.operacao.empresaSelecionada);
          console.log('Simulação carregada:', state.simulacao);
        }
      }
      telaBase.carregando = false;
    }

    watch(() => modalBase.computedVisivel, async () => {
      await atualizarTela();
    });

    watch(() => state.simulacao.profundidadeTotal, (profundidadeTotal) => {
      if (!profundidadeTotal) {
        state.simulacao.codigoTipoProducaoPI = 0;
      }
    });

    function adicionarItem() {
      if (!state.novoItem.codigoEngenharia || !state.novoItem.quantidadeSimulada) {
        apresentarMensagemAlerta('Informe o produto e a quantidade.');
        return;
      }

      const itemExistente = state.simulacao.itensSimulados.find((i: any) => i.codigoEngenharia === state.novoItem.codigoEngenharia);
      if (itemExistente) {
        itemExistente.quantidadeSimulada += state.novoItem.quantidadeSimulada;
      } else {
        state.simulacao.itensSimulados.push(JSON.parse(JSON.stringify(state.novoItem)));
      }

      Object.assign(state.novoItem, objetoNovoItem());
    }

    function removerItem(item: ISimulacaoProducaoItemLocal) {
      state.simulacao.itensSimulados = state.simulacao.itensSimulados.filter((i: any) => i.codigoEngenharia !== item.codigoEngenharia);
    }

    async function explodirNecessidades() {
      if (state.simulacao.itensSimulados.length === 0) {
        apresentarMensagemAlerta('Adicione pelo menos um item para simular.');
        return;
      }

      if (state.simulacao.profundidadeTotal && !state.simulacao.codigoTipoProducaoPI) {
        apresentarMensagemAlerta('Informe o tipo de produção para os itens PI.');
        return;
      }

      try {
        const necessidades = await servicoPCP.explodirNecessidades(
          state.simulacao.codigoEmpresa,
          state.simulacao.itensSimulados,
          state.simulacao.profundidadeTotal,
          state.simulacao.codigoTipoProducaoPI,
        );
        state.simulacao.necessidadesCalculadas = necessidades;
        apresentarMensagemSucesso('Necessidades calculadas com sucesso!');
      } catch (e) {
        apresentarMensagemErro('Erro ao calcular necessidades.');
      }
    }

    async function salvarSimulacao() {
      if (state.simulacao.itensSimulados.length === 0) {
        apresentarMensagemAlerta('Adicione pelo menos um item para salvar a simulação.');
        return;
      }
      try {
        state.simulacao.dataSimulacao = new Date().toISOString();
        const retorno = state.simulacao.codigo > 0
          ? await servicoPCP.alterarSimulacao(state.simulacao)
          : await servicoPCP.incluirSimulacao(state.simulacao);

        if (retorno.codigoRegistro) {
          state.simulacao.codigo = retorno.codigoRegistro;
          apresentarMensagemSucesso('Simulação salva com sucesso!');
          emit('update:confirmacao');
        }
      } catch (e) {
        apresentarMensagemErro('Erro ao salvar simulação.');
      }
    }

    // No setup do componente
    async function imprimirSimulacao() {
      if (state.simulacao.codigo <= 0) {
        apresentarMensagemAlerta('Salve a simulação antes de imprimir.');
        return;
      }

      try {
        // Chama o serviço passando o código da simulação e da empresa
        const retornoRelatorio = await servicoPCP.imprimirSimulacao(
          state.simulacao.codigo,
          state.simulacao.codigoEmpresa,
        );
        if (retornoRelatorio.status === EStatusRetornoRequisicao.Sucesso) {
          state.arquivosPdf = [{ nome: '', link: retornoRelatorio.link } as IArquivoPdf];
        } else if (retornoRelatorio.status === EStatusRetornoRequisicao.Alerta) {
          apresentarMensagemAlerta(retornoRelatorio.mensagem);
        } else if (retornoRelatorio.status === EStatusRetornoRequisicao.Erro) {
          apresentarMensagemErro(retornoRelatorio.mensagem);
        }
      } catch (e) {
        apresentarMensagemErro('Erro ao processar a impressão da simulação.');
      }
    }

    preencherColunas();

    return {
      telaBase,
      modalBase,
      state,
      gradeItens,
      gradeNecessidades,
      adicionarItem,
      removerItem,
      explodirNecessidades,
      salvarSimulacao,
      UtilitarioMascara,
      ECustomRenderRow,
      formattedDataSimulacao,
      custoTotalNecessario,
      colunasItens,
      colunasNecessidades,
      imprimirSimulacao,
    };
  },
});
